import { useId } from "react";
import { DensityToggle, RadioGroup, RadioGroupItem, SettingRow, SettingsSection, cn, type AppearanceMenuLabels } from "@fox1ck-org/ui";
import { THEME_SETTINGS, useTheme, type Density, type ThemeSetting } from "@fox1ck-org/ui/theme";

import { useT } from "@/lib/i18n";

/**
 * Where the preference lives: this browser. The key predates the shell, so a
 * person who already picked a theme keeps it (decision 01 — «у тех, кто выбрал
 * светлую, выбор сохраняется»); `vite.config.ts` stamps it before first paint.
 */
export const THEME_STORAGE_KEY = "vibe-tracker-theme";
/**
 * Until a person picks a theme the brand's `default_theme` (console data) wins
 * — main.tsx and the pre-paint stamp read it. This is only the cold paint
 * before any brand answer is cached, so it is the brand default's light: a
 * first visit does not flash dark and then turn light (board TEdges).
 * Density stays compact (decision 02). `vite.config.ts` repeats both.
 */
export const DEFAULT_THEME: ThemeSetting = "light";
export const DEFAULT_DENSITY: Density = "compact";

const THEME_KEY = {
  dark: "appearance.theme.dark",
  light: "appearance.theme.light",
  black: "appearance.theme.black",
  system: "appearance.theme.system",
} as const;

const THEME_HINT_KEY = {
  dark: "appearance.theme.dark.hint",
  light: "appearance.theme.light.hint",
  black: "appearance.theme.black.hint",
} as const;

const DENSITY_METRICS_KEY = {
  compact: "appearance.density.compact.metrics",
  default: "appearance.density.default.metrics",
  comfortable: "appearance.density.comfortable.metrics",
} as const;

/** The order the boards draw the tiers in: the product's own theme first. */
const TIERS: readonly ThemeSetting[] = ["dark", "light", "black", "system"];

/** The words `AppearanceMenu` and `DensityToggle` own, in Russian. */
export function useAppearanceLabels(): AppearanceMenuLabels {
  const t = useT();
  return {
    trigger: t("appearance.title"),
    collapsedTrigger: (tier) => t("appearance.trigger", { tier }),
    theme: t("appearance.theme"),
    themes: {
      dark: t(THEME_KEY.dark),
      light: t(THEME_KEY.light),
      black: t(THEME_KEY.black),
      system: t(THEME_KEY.system),
    },
    systemHint: t("appearance.systemHint"),
    density: t("appearance.density"),
    densityGroup: t("appearance.density"),
    densities: {
      compact: t("appearance.density.compact"),
      default: t("appearance.density.default"),
      comfortable: t("appearance.density.comfortable"),
    },
  };
}

/**
 * Настройки → Оформление (доска Settings): the same two choices the rail's
 * button opens, laid out for reading — four tiers with a picture of each, and
 * the density with what it buys. Both write through `ThemeProvider`, so the
 * rail and this screen are one value, kept across a reload.
 */
export function AppearanceSettings() {
  const t = useT();
  const labels = useAppearanceLabels();
  const { theme, setTheme, density, setDensity, resolvedTheme } = useTheme();
  const groupId = useId();
  return (
    <SettingsSection title={t("appearance.title")}>
      <SettingRow label={t("appearance.theme")} description={t("appearance.theme.description")} controlId={groupId}>
        <RadioGroup
          id={groupId}
          aria-label={t("appearance.theme")}
          value={theme}
          onValueChange={(v) => {
            if ((THEME_SETTINGS as readonly string[]).includes(v)) setTheme(v as ThemeSetting);
          }}
          className="grid grid-cols-4 gap-3"
        >
          {TIERS.map((tier) => {
            const selected = tier === theme;
            const painted = tier === "system" ? (resolvedTheme ?? "dark") : tier;
            const hint =
              tier === "system"
                ? t("appearance.theme.system.hint", { tier: (labels.themes?.[resolvedTheme ?? "dark"] ?? "").toLowerCase() })
                : t(THEME_HINT_KEY[tier as keyof typeof THEME_HINT_KEY]);
            return (
              <label
                key={tier}
                className={cn(
                  "flex cursor-pointer flex-col gap-2 rounded-md border p-2 transition-colors",
                  selected ? "border-primary bg-primary-soft" : "border-border hover:bg-surface-2",
                )}
              >
                <ThemePreview tier={painted} />
                <span className="flex items-start gap-2">
                  <RadioGroupItem value={tier} className="mt-0.5" />
                  <span className="flex min-w-0 flex-col">
                    <span className="text-sm font-medium text-foreground">{labels.themes?.[tier]}</span>
                    <span className="text-xs text-muted-foreground">{hint}</span>
                  </span>
                </span>
              </label>
            );
          })}
        </RadioGroup>
      </SettingRow>
      <SettingRow label={t("appearance.density")} description={t("appearance.density.description")}>
        <div className="flex flex-col gap-1.5">
          <DensityToggle value={density} onValueChange={setDensity} label={t("appearance.density")} labels={labels.densities} showLabels />
          <span className="text-xs text-muted-foreground">{t(DENSITY_METRICS_KEY[density])}</span>
        </div>
      </SettingRow>
    </SettingsSection>
  );
}

/**
 * A thumbnail of a tier, painted by the tier itself: the box carries
 * `data-theme`, so the tokens inside it resolve to that theme's values and the
 * picture cannot drift from the real thing.
 */
function ThemePreview({ tier }: { tier: "light" | "dark" | "black" }) {
  return (
    <span aria-hidden data-theme={tier} className="flex h-23 overflow-hidden rounded-sm border border-border bg-background">
      <span className="flex w-1/4 flex-col gap-1.5 border-r border-border bg-sidebar p-1.5">
        <span className="h-1.5 w-full rounded-full bg-primary" />
        <span className="h-1 w-3/4 rounded-full bg-surface-3" />
        <span className="h-1 w-3/4 rounded-full bg-surface-3" />
      </span>
      <span className="flex flex-1 flex-col gap-1.5 p-1.5">
        <span className="h-1.5 w-1/2 rounded-full bg-foreground" />
        <span className="flex flex-1 flex-col gap-1.5 rounded-sm border border-border bg-card p-1.5">
          <span className="h-1 w-full rounded-full bg-surface-3" />
          <span className="h-1 w-2/3 rounded-full bg-ok" />
          <span className="h-1 w-1/3 rounded-full bg-destructive" />
        </span>
      </span>
    </span>
  );
}
